/**
 * One unlocked Veto per browser profile (review H3).
 *
 * Two unlocked instances (two tabs, or the installed PWA window + a tab) each
 * cached their own copy of every ratchet and advanced it independently: both
 * consumed the same message keys, re-used (key, nonce) pairs on send and
 * overwrote each other's saved state. Now unlocking holds an exclusive Web Lock.
 * Unlocking in a second window *takes over*: the lock is stolen, the first
 * window finishes its in-flight session writes, locks itself and confirms on a
 * BroadcastChannel before the new window starts touching sessions.
 */

const LOCK_NAME = "veto-active-instance";
const CHANNEL_NAME = "veto-instance";
const HANDOVER_TIMEOUT_MS = 2_000;

type LockManagerLike = {
  request: (name: string, options: { steal?: boolean }, cb: () => Promise<void>) => Promise<unknown>;
  query?: () => Promise<{ held?: { name?: string }[] }>;
};

let releaseHeld: (() => void) | null = null;
let lostHandler: (() => Promise<void> | void) | null = null;
let lockedElsewhere = false;

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL_NAME) : null;

function lockManager(): LockManagerLike | null {
  const nav = globalThis.navigator as (Navigator & { locks?: LockManagerLike }) | undefined;
  return nav?.locks && typeof nav.locks.request === "function" ? nav.locks : null;
}

/** Called (once) when another window takes over: drain work, then lock. */
export function setInstanceLostHandler(handler: (() => Promise<void> | void) | null): void {
  lostHandler = handler;
}

/** Is another window currently unlocked? */
export async function otherInstanceActive(): Promise<boolean> {
  if (releaseHeld) return false;
  const locks = lockManager();
  if (!locks?.query) return false;
  try {
    const state = await locks.query();
    return (state.held ?? []).some((l) => l.name === LOCK_NAME);
  } catch {
    return false;
  }
}

/** True once after this window was locked because another one took over. */
export function consumeLockedElsewhere(): boolean {
  const v = lockedElsewhere;
  lockedElsewhere = false;
  return v;
}

function waitForHandover(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      channel?.removeEventListener("message", onMessage);
      resolve();
    };
    const onMessage = (e: MessageEvent) => {
      if ((e.data as { t?: string } | null)?.t === "released") done();
    };
    const timer = setTimeout(done, ms);
    channel?.addEventListener("message", onMessage);
  });
}

async function onStolen(): Promise<void> {
  releaseHeld = null;
  lockedElsewhere = true;
  try {
    await lostHandler?.();
  } finally {
    channel?.postMessage({ t: "released" });
  }
}

/**
 * Become the active instance before any session is touched. Resolves once the
 * previous holder (if any) has confirmed it stopped, or after a short timeout.
 * No-op where Web Locks are unavailable (single-window native shells).
 */
export async function takeInstanceLease(): Promise<void> {
  const locks = lockManager();
  if (!locks || releaseHeld) return;
  const busy = await otherInstanceActive();
  const handedOver = busy ? waitForHandover(HANDOVER_TIMEOUT_MS) : Promise.resolve();
  await new Promise<void>((granted) => {
    locks
      .request(LOCK_NAME, { steal: true }, () =>
        new Promise<void>((release) => {
          releaseHeld = () => {
            releaseHeld = null;
            release();
          };
          granted();
        }),
      )
      .catch((err: unknown) => {
        if ((err as { name?: string } | null)?.name === "AbortError") void onStolen();
        else granted(); // lock manager failure: do not block the user
      });
  });
  await handedOver;
}

/** Vault locked: let another window unlock without stealing. */
export function releaseInstanceLease(): void {
  releaseHeld?.();
}
